import React, { useEffect, useState } from "react";

import {
  fetchProductAccessAudit,
  fetchProductAccessCompanies,
  fetchProductAccessCompany,
  fetchProductAccessUsers,
  updateCompanyEntitlement,
  updateUserProductProfile,
} from "./productAccessApi";
import AiWorkAccessManager from "./AiWorkAccessManager";

const PRODUCT_LABELS = Object.freeze({
  director: "Ampli Diretoria",
});

const STATE_LABELS = Object.freeze({
  off: "Desativado",
  trial: "Trial",
  active: "Ativo",
  suspended: "Suspenso",
});

const safeError = (error, fallback = "Não foi possível concluir a operação.") => ({
  code: typeof error?.code === "string" ? error.code : "request_failed",
  message: typeof error?.message === "string" ? error.message : fallback,
  retryable: error?.retryable === true,
});

const dateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const endOfDate = (value) => (
  value ? new Date(`${value}T23:59:59.999Z`).toISOString() : null
);

const parsedDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? false : date;
};

const effectiveEntitlement = (entitlement, now) => {
  const validFrom = parsedDate(entitlement?.validFrom);
  const validUntil = parsedDate(entitlement?.validUntil);
  const withinWindow = validFrom !== false
    && validUntil !== false
    && (!validFrom || validFrom <= now)
    && (!validUntil || validUntil > now);
  const enabled = withinWindow && (
    entitlement?.state === "active"
    || (entitlement?.state === "trial" && validUntil instanceof Date)
  );
  return { ...entitlement, enabled };
};

const directorContractStatus = (company) => {
  if (company?.entitlements?.director?.enabled !== true) {
    return STATE_LABELS[company?.entitlements?.director?.state]
      ?? company?.entitlements?.director?.state;
  }
  if (company.directorProjectCount === 0) return "Contratado sem obras";
  if (company.directorUserCount === 0) return "Obra contratada sem usuários";
  return STATE_LABELS[company.entitlements.director.state];
};

const activeDirectorUser = (user, now) => {
  const validUntil = parsedDate(user?.validUntil);
  return user?.active !== false
    && user?.profile === "director"
    && user?.profileState === "active"
    && validUntil !== false
    && (!validUntil || validUntil > now);
};

const EntitlementForm = ({
  companyId,
  product,
  entitlement,
  projects,
  onSaved,
  onConflict,
  onMutationError,
}) => {
  const label = PRODUCT_LABELS[product];
  const [state, setState] = useState(entitlement.state);
  const [validUntil, setValidUntil] = useState(dateInputValue(entitlement.validUntil));
  const [reason, setReason] = useState("");
  const [projectIds, setProjectIds] = useState(
    Array.isArray(entitlement.projectIds) ? entitlement.projectIds : [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const valid = reason.trim().length > 0 && (state !== "trial" || Boolean(validUntil));

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    onMutationError(null);
    setSaved(false);
    try {
      const result = await updateCompanyEntitlement(companyId, {
        product,
        state,
        ...(product === "director" ? { projectIds } : {}),
        validFrom: null,
        validUntil: state === "trial" ? endOfDate(validUntil) : null,
        reason: reason.trim(),
        expectedVersion: entitlement.version,
      });
      onSaved(product, result.entitlement);
      setReason("");
      setSaved(true);
    } catch (requestError) {
      const normalized = safeError(requestError);
      onMutationError(normalized);
      if (requestError?.status === 409 || normalized.code === "version_conflict") {
        await onConflict();
      }
      setError(normalized);
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset className="product-access-card" aria-label={label}>
      <legend>{label}</legend>
      <div className="product-access-card__status">
        <span className={`integrations-chip${entitlement.enabled ? " integrations-chip--on" : ""}`}>
          {STATE_LABELS[entitlement.state] ?? entitlement.state}
        </span>
        <small>Versão {entitlement.version}</small>
      </div>
      <label htmlFor={`entitlement-state-${product}`}>Estado do {label}</label>
      <select
        id={`entitlement-state-${product}`}
        value={state}
        onChange={(event) => {
          setState(event.target.value);
          setSaved(false);
          if (event.target.value !== "trial") setValidUntil("");
        }}
      >
        {Object.entries(STATE_LABELS).map(([value, text]) => (
          <option key={value} value={value}>{text}</option>
        ))}
      </select>

      {state === "trial" ? (
        <>
          <label htmlFor={`entitlement-valid-until-${product}`}>Fim do trial do {label}</label>
          <input
            id={`entitlement-valid-until-${product}`}
            type="date"
            required
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </>
      ) : null}

      {product === "director" ? (
        <div className="product-access-card__projects">
          <span>Obras contratadas para a Diretoria</span>
          {projects.length === 0 ? <p>Nenhuma obra ativa elegível.</p> : projects.map((project) => (
            <label key={project._id}>
              <input
                type="checkbox"
                checked={projectIds.includes(project._id)}
                onChange={(event) => {
                  setProjectIds((current) => (
                    event.target.checked
                      ? [...current, project._id]
                      : current.filter((projectId) => projectId !== project._id)
                  ));
                  setSaved(false);
                }}
              />
              {project.name || "Obra sem nome"}
            </label>
          ))}
          {entitlement.enabled && projectIds.length === 0 ? (
            <p role="status">
              <strong>Contratado sem obras.</strong> Nenhum usuário terá acesso efetivo à Diretoria até uma obra ser selecionada.
            </p>
          ) : null}
        </div>
      ) : null}

      <label htmlFor={`entitlement-reason-${product}`}>Motivo da alteração do {label}</label>
      <textarea
        id={`entitlement-reason-${product}`}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
          setSaved(false);
        }}
        placeholder="Registre a decisão comercial ou operacional"
      />
      {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
      {saved ? <p className="product-access-success" role="status">Alteração salva.</p> : null}
      <button type="button" disabled={!valid || saving} onClick={save}>
        {saving ? "Salvando…" : `Salvar ${label}`}
      </button>
    </fieldset>
  );
};

const UserAccessForm = ({
  companyId,
  user,
  onSaved,
  onConflict,
  onMutationError,
}) => {
  const [profile, setProfile] = useState(user.profile ?? "works");
  const [profileState, setProfileState] = useState(
    user.profileState === "default" ? "active" : user.profileState,
  );
  const [validUntil, setValidUntil] = useState(dateInputValue(user.validUntil));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const valid = reason.trim().length > 0;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    onMutationError(null);
    try {
      const result = await updateUserProductProfile(user._id, companyId, {
        profile,
        state: profileState,
        validUntil: endOfDate(validUntil),
        reason: reason.trim(),
        expectedVersion: user.profileVersion,
      });
      onSaved(user._id, result.profile);
      setReason("");
    } catch (requestError) {
      const normalized = safeError(requestError);
      onMutationError(normalized);
      if (requestError?.status === 409 || normalized.code === "version_conflict") {
        await onConflict();
      }
      setError(normalized);
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset className="product-access-user" aria-label={`Acesso de ${user.name}`}>
      <legend>
        <b>{user.name}</b>
        <span>{user.email}</span>
      </legend>
      <div className="product-access-user__fields">
        <label htmlFor={`profile-${user._id}`}>Perfil de {user.name}</label>
        <select
          id={`profile-${user._id}`}
          value={profile}
          onChange={(event) => {
            setProfile(event.target.value);
          }}
        >
          <option value="works">Obras</option>
          <option value="director">Diretoria</option>
        </select>

        <label htmlFor={`profile-state-${user._id}`}>Estado do acesso de {user.name}</label>
        <select
          id={`profile-state-${user._id}`}
          value={profileState}
          onChange={(event) => setProfileState(event.target.value)}
        >
          <option value="active">Ativo</option>
          <option value="inactive">Inativo</option>
        </select>

        <label htmlFor={`profile-valid-until-${user._id}`}>Validade do acesso de {user.name}</label>
        <input
          id={`profile-valid-until-${user._id}`}
          type="date"
          value={validUntil}
          onChange={(event) => setValidUntil(event.target.value)}
        />

        <label htmlFor={`profile-reason-${user._id}`}>Motivo da alteração de {user.name}</label>
        <textarea
          id={`profile-reason-${user._id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Registre por que este perfil está sendo alterado"
        />
      </div>

      <div className="product-access-user__footer">
        <small>Versão {user.profileVersion}</small>
        {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
        <button
          type="button"
          disabled={!valid || saving}
          onClick={save}
          aria-label={`Salvar acesso de ${user.name}`}
        >
          {saving ? "Salvando…" : "Salvar acesso"}
        </button>
      </div>
    </fieldset>
  );
};

const History = ({ items }) => (
  <section className="product-access-history" aria-label="Histórico de alterações">
    <h3>Histórico de alterações</h3>
    {items.length === 0 ? <p>Nenhuma alteração registrada para esta empresa.</p> : (
      <ol>
        {items.map((item) => {
          const companyChange = item.action === "company_entitlement_changed";
          const directorChange = companyChange && Array.isArray(item.after?.projectIds);
          const userChange = item.action === "user_product_profile_changed";
          return <li key={item._id}>
            <div>
              <b>{directorChange
                ? "Contrato da Diretoria alterado"
                : companyChange
                  ? "Produto da empresa alterado"
                  : userChange
                    ? "Perfil de usuário alterado"
                    : "Alteração registrada"}</b>
              {directorChange ? (
                <span>{`${item.after.projectIds.length} ${item.after.projectIds.length === 1 ? "obra contratada" : "obras contratadas"}`}</span>
              ) : userChange ? (
                <span>{`Perfil ${item.after?.profile === "director" ? "Diretoria" : "Obras"}`}</span>
              ) : null}
              <span>{item.reason}</span>
            </div>
            <small>
              {item.actorId} · {item.createdAt
                ? new Date(item.createdAt).toLocaleString("pt-BR")
                : "data indisponível"}
            </small>
          </li>;
        })}
      </ol>
    )}
  </section>
);

const ProductAccess = ({ now = () => new Date() }) => {
  const [companies, setCompanies] = useState(null);
  const [query, setQuery] = useState("");
  const [listError, setListError] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const effectiveNow = now();

  const loadCompanies = async () => {
    setListError(null);
    setCompanies(null);
    try {
      const result = await fetchProductAccessCompanies();
      setCompanies(result.companies ?? []);
    } catch (error) {
      setListError(safeError(error, "Não foi possível listar produtos e acessos."));
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanyDetail = async (companyId) => {
    setSelectedCompanyId(companyId);
    setDetail(null);
    setDetailError(null);
    setMutationError(null);
    setDetailLoading(true);
    try {
      const [companyResult, usersResult, auditResult] = await Promise.all([
        fetchProductAccessCompany(companyId),
        fetchProductAccessUsers(companyId),
        fetchProductAccessAudit(companyId),
      ]);
      setDetail({
        company: companyResult.company,
        users: usersResult.users ?? [],
        audit: auditResult.items ?? [],
      });
    } catch (error) {
      setDetailError(safeError(error, "Não foi possível carregar os acessos da empresa."));
    } finally {
      setDetailLoading(false);
    }
  };

  const reloadCompany = async () => {
    if (!selectedCompanyId) return;
    const result = await fetchProductAccessCompany(selectedCompanyId);
    setDetail((current) => current ? { ...current, company: result.company } : current);
  };

  const entitlementSaved = (product, rawEntitlement) => {
    const nextEntitlement = effectiveEntitlement(rawEntitlement, now());
    setDetail((current) => ({
      ...current,
      company: {
        ...current.company,
        entitlements: {
          ...current.company.entitlements,
          [product]: nextEntitlement,
        },
      },
    }));
    setCompanies((current) => current?.map((company) => (
      company._id === selectedCompanyId
        ? {
          ...company,
          entitlements: { ...company.entitlements, [product]: nextEntitlement },
          ...(product === "director" ? {
            directorProjectCount: nextEntitlement.projectIds?.length ?? 0,
          } : {}),
        }
        : company
    )));
  };

  const userSaved = (userId, profile) => {
    setDetail((current) => {
      if (!current) return current;
      return {
        ...current,
        users: current.users.map((user) => (
          user._id === userId
            ? {
              ...user,
              profile: profile.profile,
              profileState: profile.state,
              profileVersion: profile.version,
              validUntil: profile.validUntil,
            }
            : user
        )),
      };
    });
  };

  const visibleCompanies = (companies ?? [])
    .filter((company) => (
      !query || company.name.toLowerCase().includes(query.toLowerCase())
    ))
    .map((company) => (
      company._id === selectedCompanyId && detail
        ? {
          ...company,
          directorUserCount: detail.users
            .filter((user) => activeDirectorUser(user, effectiveNow)).length,
        }
        : company
    ));

  return (
    <div className="integrations-page product-access-page">
      <header className="product-access-page__header">
        <div>
          <p className="product-access-eyebrow">Controle comercial</p>
          <h2>Produtos e acessos</h2>
          <p>Ative a Diretoria por empresa, configure a IA por obra e defina os perfis dos usuários.</p>
        </div>
      </header>

      <div className="integrations-controls">
        <label htmlFor="product-access-query">Filtrar empresas</label>
        <input
          id="product-access-query"
          type="search"
          placeholder="Filtrar por empresa…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {companies === null && !listError ? <p>Carregando produtos e acessos…</p> : null}
      {listError ? (
        <div className="product-access-error-panel" role="alert">
          <p>{listError.message}</p>
          {listError.retryable ? (
            <button type="button" onClick={loadCompanies}>Tentar novamente</button>
          ) : null}
        </div>
      ) : null}

      {companies ? (
        <div className="integrations-scroll">
          <table className="integrations-table product-access-companies">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Ampli Diretoria</th>
                <th>IA contextual</th>
                <th className="num">Obras</th>
                <th className="num">Diretores</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleCompanies.map((company) => (
                <tr key={String(company._id)}>
                  <td><b>{company.name}</b></td>
                  <td>{directorContractStatus(company)}</td>
                  <td>Configuração por obra</td>
                  <td className="num">{company.directorProjectCount}</td>
                  <td className="num">{company.directorUserCount}</td>
                  <td>
                    <button
                      type="button"
                      aria-label={`Gerenciar ${company.name}`}
                      onClick={() => loadCompanyDetail(company._id)}
                    >
                      Gerenciar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {detailLoading ? <p className="product-access-detail-loading">Carregando acessos da empresa…</p> : null}
      {detailError ? <p className="product-access-error" role="alert">{detailError.message}</p> : null}
      {mutationError ? <p className="product-access-error" role="alert">{mutationError.message}</p> : null}

      {detail ? (
        <section className="product-access-detail" aria-label={`Produtos e acessos de ${detail.company.name}`}>
          <header>
            <div>
              <p className="product-access-eyebrow">Empresa selecionada</p>
              <h2>{detail.company.name}</h2>
            </div>
            <button type="button" onClick={() => loadCompanyDetail(detail.company._id)}>
              Recarregar dados
            </button>
          </header>

          <div className="product-access-products">
            {Object.keys(PRODUCT_LABELS).map((product) => (
              <EntitlementForm
                key={`${product}-${detail.company.entitlements[product].version}`}
                companyId={detail.company._id}
                product={product}
                entitlement={detail.company.entitlements[product]}
                projects={detail.company.directorProjects ?? []}
                onSaved={entitlementSaved}
                onConflict={reloadCompany}
                onMutationError={setMutationError}
              />
            ))}
          </div>

          {detail.company.entitlements.director.enabled
            && (detail.company.entitlements.director.projectIds?.length ?? 0) > 0
            && !detail.users.some((user) => activeDirectorUser(user, effectiveNow)) ? (
              <p className="product-access-card__warning" role="status">
                <strong>Obra contratada sem usuários.</strong> Ative o perfil Diretoria em pelo menos um usuário elegível.
              </p>
            ) : null}

          <AiWorkAccessManager
            key={detail.company._id}
            companyId={detail.company._id}
          />

          <section className="product-access-users" aria-label="Acessos dos usuários">
            <h3>Acessos dos usuários</h3>
            {detail.users.map((user) => (
              <UserAccessForm
                key={`${user._id}-${user.profileVersion}`}
                companyId={detail.company._id}
                user={user}
                onSaved={userSaved}
                onConflict={() => loadCompanyDetail(detail.company._id)}
                onMutationError={setMutationError}
              />
            ))}
          </section>

          <History items={detail.audit} />
        </section>
      ) : null}
    </div>
  );
};

export default ProductAccess;
